﻿import type {
  Issuer,
} from "../types";

import type {
  ApiResponse,
} from "@/scripts/Common/api";
import {
  defaultFetch,
  httpMethod,
} from "@/scripts/Common/api";

import {
  getToken,
} from "@/scripts/Common";

import type {
  IssuerCard,
} from "@/components/issuers/scripts/issuerCards";

import type {
  CreateIssuerResult,
  FetchIssuersParam
} from "./types";

import {
  cleanBankAccountForApi,
} from "@/scripts/Common/helpers/apiTransformations";

const fetchIssuers = async (args: FetchIssuersParam = {}, abortSignal? : AbortSignal): Promise<ApiResponse<Issuer[]>> => {

  const {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  } = await defaultFetch({
    apiName: 'issuersV2',
    bearer: getToken(),
    options: {
      query: {
        ...args,
        pageNumber: typeof args?.pageNumber === 'number' ? args.pageNumber - 1 : undefined, // reduce by 1 to make it 0 based
      },
      abortSignal
    }
  });

  return {
    response: response as Issuer[],
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  }
}

const fetchIssuer = async (id: number): Promise<ApiResponse<Issuer>> => {

  // validate arguments
  if (id <= 0) {
    throw new Error('Id must be greater than 0.');
  }

  const {
    response,
    error,
  } = await defaultFetch({
    apiName: 'issuersWithParams',
    bearer: getToken(),
    options: {
      routeParams: {
        param: id.toString()
      }
    }
  });

  return {
    response: response as Issuer,
    error,
  }
}

/**
 * Cleans up issuer object by converting null/undefined values in some objects to empty strings for API compatibility
 * @param issuer - The issuer object to clean
 * @returns Cleaned issuer object
 */
const cleanIssuerForApi = (issuer: Issuer): Issuer => {
  return {
    ...issuer,
    businessBankAccount: cleanBankAccountForApi(issuer.businessBankAccount) as typeof issuer.businessBankAccount,
  };
};

const createOrUpdateIssuer = async (issuer: Issuer, method: httpMethod.PUT | httpMethod.POST)
  : Promise<ApiResponse<CreateIssuerResult>> => {

  const cleanedIssuer = cleanIssuerForApi(issuer);

  return await defaultFetch({
    apiName: 'issuers',
    options: {
      method
    },
    body: JSON.stringify(cleanedIssuer),
    bearer: getToken()
  });
};

const updateIssuerState = async (issuer: Issuer, param: string)
  : Promise<ApiResponse<number>> => {

  const cleanedIssuer = cleanIssuerForApi(issuer);

  return await defaultFetch({
    apiName: 'issuersWithParams',
    options: {
      method: httpMethod.PUT,
      routeParams: {
        param
      }
    },
    body: JSON.stringify(cleanedIssuer),
    bearer: getToken()
  });
};

const fetchIssuerCards = async (issuerId: number)  : Promise<ApiResponse<IssuerCard[]>>  =>
  await defaultFetch({
    apiName: 'getIssuerCardList',
    bearer: getToken(),
    options: {
      query: {
        issuerId: issuerId,
        pageNumber: 0,
        pageSize: 1,
      }
    }
  })

const deleteIssuer = async (issuerId: number) => {
  const {
    error,
  } = await defaultFetch({
    apiName: 'issuers',
    options: {
      method: httpMethod.DELETE,
      query: {
        issuerId
      }
    },
    bearer: getToken()
  });

  return {
    error,
  };
}

export {
  fetchIssuers,
  fetchIssuer,
  createOrUpdateIssuer,
  updateIssuerState,
  fetchIssuerCards,
  deleteIssuer
}
